import React from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";
import {
  Code2,
  PenLine,
  ImageIcon,
  ArrowLeft,
  Sparkles,
  Brain,
  Check,
  Clock,
  Languages,
} from "lucide-react";

const BRAINS = [
  {
    id: "claude",
    name: "Claude Sonnet 4.5",
    provider: "Anthropic",
    icon: Code2,
    accent: "from-orange-500/20",
    badge: "bg-orange-500/15 text-orange-300",
    pickProvider: "anthropic",
  },
  {
    id: "gpt",
    name: "GPT-5.2",
    provider: "OpenAI",
    icon: PenLine,
    accent: "from-emerald-500/20",
    badge: "bg-emerald-500/15 text-emerald-300",
    pickProvider: "openai",
  },
  {
    id: "gemini",
    name: "Gemini 3 Pro",
    provider: "Google",
    icon: ImageIcon,
    accent: "from-sky-500/20",
    badge: "bg-sky-500/15 text-sky-300",
    pickProvider: "gemini",
  },
];

export default function Brains() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="border-b border-slate-100 bg-gradient-to-b from-[#9ACD32]/10 via-white to-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-10">
          <Link to="/chat" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#7AAA20] hover:text-[#9ACD32]" data-testid="brains-back">
            <ArrowLeft className="h-3 w-3" />
            {t("brains.back")}
          </Link>
          <div className="mt-5 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0B0E5C]/5 text-[#0B0E5C]">
              <Brain className="h-6 w-6" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#7AAA20]">
              {t("brains.pretitle")}
            </span>
          </div>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-[#0B0E5C] sm:text-4xl lg:text-5xl">
            {t("brains.title")}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-slate-600 sm:text-lg">{t("brains.subtitle")}</p>
        </div>
      </section>

      {/* Brain cards */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
        <div className="grid gap-6 lg:grid-cols-3">
          {BRAINS.map((b) => {
            const Icon = b.icon;
            return (
              <Card key={b.id} className={`relative overflow-hidden border-slate-200 bg-gradient-to-br ${b.accent} to-white p-6`} data-testid={`brains-card-${b.id}`}>
                <div className="flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#0B0E5C] shadow-sm">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${b.badge}`}>
                    {b.provider}
                  </span>
                </div>
                <h2 className="mt-4 font-display text-xl font-extrabold text-[#0B0E5C]">{b.name}</h2>
                <p className="mt-1 text-sm font-bold text-[#7AAA20]">{t(`brains.${b.id}.tagline`)}</p>

                <h3 className="mt-5 text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("brains.strengths")}</h3>
                <ul className="mt-2 space-y-1.5">
                  {[1,2,3,4].map((i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#7AAA20]" />
                      <span>{t(`brains.${b.id}.strength_${i}`)}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="mt-5 text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("brains.try_example")}</h3>
                <p className="mt-2 rounded-lg bg-white/80 p-3 text-xs italic leading-relaxed text-slate-600">
                  {`"${t(`brains.${b.id}.example`)}"`}
                </p>

                <Link
                  to={`/chat?provider=${b.pickProvider}`}
                  className="mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#0B0E5C] py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0F1370]"
                  data-testid={`brains-pick-${b.id}`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("brains.pick_btn")}
                </Link>
              </Card>
            );
          })}
        </div>

        {/* Comparison table — desktop only, cards above are mobile-friendly */}
        <div className="mt-12 hidden overflow-hidden rounded-2xl border border-slate-200 lg:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">{t("brains.col_dimension")}</th>
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-orange-700">Claude</th>
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-emerald-700">GPT</th>
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-sky-700">Gemini</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {["best_for", "speed", "context", "languages", "style"].map((row) => (
                <tr key={row}>
                  <td className="px-5 py-3 font-semibold text-[#0B0E5C]">{t(`brains.row_${row}`)}</td>
                  <td className="px-5 py-3 text-slate-700">{t(`brains.claude.${row}`)}</td>
                  <td className="px-5 py-3 text-slate-700">{t(`brains.gpt.${row}`)}</td>
                  <td className="px-5 py-3 text-slate-700">{t(`brains.gemini.${row}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Reassurance footer */}
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          <Card className="border-slate-200 p-5">
            <Clock className="h-5 w-5 text-[#7AAA20]" />
            <p className="mt-2 text-sm font-bold text-[#0B0E5C]">{t("brains.reassure_switch_title")}</p>
            <p className="mt-1 text-xs text-slate-600">{t("brains.reassure_switch_desc")}</p>
          </Card>
          <Card className="border-slate-200 p-5">
            <Sparkles className="h-5 w-5 text-[#7AAA20]" />
            <p className="mt-2 text-sm font-bold text-[#0B0E5C]">{t("brains.reassure_auto_title")}</p>
            <p className="mt-1 text-xs text-slate-600">{t("brains.reassure_auto_desc")}</p>
          </Card>
          <Card className="border-slate-200 p-5">
            <Languages className="h-5 w-5 text-[#7AAA20]" />
            <p className="mt-2 text-sm font-bold text-[#0B0E5C]">{t("brains.reassure_lang_title")}</p>
            <p className="mt-1 text-xs text-slate-600">{t("brains.reassure_lang_desc")}</p>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}
